import { createFileRoute } from '@tanstack/react-router'
import { lazy, Suspense, useEffect, useState } from 'react'
import { useMotionValue } from 'motion/react'
import { BODY_FONT, HEADING_FONT, horizontalScrim } from '@/lib/constants'
import { COLORS, GUTTER, NEUTRAL8_RGB } from '@/components/home/tokens'
import { useIsMobile } from '@/lib/hooks'
import { HubSpotEmbedForm } from '@/components/HubSpotForm'
import { type CymaticShape } from '@/components/CymaticInteractive'

const CymaticInteractive = lazy(() => import('@/components/CymaticInteractive'))

// Partner lead capture. Deliberately unlisted: reachable only by direct link
// (no nav item, no footer entry), kept out of sitemap.xml and served
// `noindex, nofollow` — see UNLISTED_ROUTES in src/lib/seo.ts.

// HubSpot form configuration — the Partner Lead form. Built in HubSpot's current
// editor, so it uses the .hs-form-frame embed (portal/region default to the
// shared values in HubSpotForm).
const HUBSPOT_FORM_ID = '2546ca04-4ea2-43ef-80ea-e19ebf88456b'

// Random Chladni mode pair picked on load — same framework as /contact.
const SHAPE_POOL: [number, number][] = [[2, 1], [3, 1], [3, 2], [4, 1], [2, 2]]
function randomShape(): CymaticShape {
  const shuffled = [...SHAPE_POOL].sort(() => Math.random() - 0.5)
  return { modes: [shuffled[0], shuffled[1]] }
}

export const Route = createFileRoute('/partners')({
  component: PartnersPage,
})

// Shared by the two supporting paragraphs under the heading (the copy is two
// paragraphs, not one block with a line break).
const bodyCopy: React.CSSProperties = {
  fontFamily: BODY_FONT,
  fontSize: 'var(--text-base)',
  lineHeight: 1.55,
  color: COLORS.neutral4,
  maxWidth: '44ch',
}

function PartnersPage() {
  const isMobile = useIsMobile()

  // Cymatic backdrop — same framework as /contact: a random Chladni figure
  // forms softly on load; static (not scroll-driven), reduced-motion aware.
  const [cymaticShape] = useState(randomShape)
  const cymaticScroll = useMotionValue(1)
  const [reducedMotion, setReducedMotion] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    // eslint-disable-next-line react-hooks/set-state-in-effect -- subscribing to a media query. Seeding from `mq.matches` is the initial read of the same external system the listener below subscribes to; splitting them would leave the first paint wrong.
    setReducedMotion(mq.matches)
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  return (
    <>
      {/* Cymatic backdrop — fixed full-viewport behind the page content (the
          form sits above it); a vertical gradient settles the particles into
          the page bg toward the bottom. */}
      {!reducedMotion && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
          <Suspense fallback={null}>
            <CymaticInteractive
              scrollProgress={cymaticScroll}
              shape={cymaticShape}
              resolveAnimation={{ from: 0, to: 0.5, durationMs: 1400 }}
              particleColor="#564F44"
            />
          </Suspense>
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              background: `linear-gradient(180deg, rgba(7,54,28,0) 0%, rgba(${NEUTRAL8_RGB},0.5) 68%, rgba(${NEUTRAL8_RGB},1) 100%)`,
            }}
          />
          {/* Horizontal scrim — solid left fading to transparent right; anchors
              the left-aligned heading over the cymatic. */}
          <div
            aria-hidden
            style={{ position: 'absolute', inset: 0, background: horizontalScrim(NEUTRAL8_RGB) }}
          />
        </div>
      )}
      <div
        style={{
          minHeight: '100vh',
          // Matches /contact, with one necessary difference: the desktop top and
          // bottom padding. /contact uses `0 ${GUTTER}` and relies on vertical
          // centering to clear the fixed 88px header, which works only because
          // its form is shorter than the viewport. The partner form is ~1078px —
          // taller than the viewport — so centering resolves to a zero offset
          // and the first fields end up under the header. The padding guarantees
          // the clearance; centering still applies whenever there's room.
          padding: isMobile ? `15vh 24px 100px` : `136px ${GUTTER} 100px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 1,
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            // Top-aligned, not centered like /contact: the form is ~1078px, so
            // centering the short text column against it strands the heading
            // halfway down the page.
            alignItems: 'flex-start',
            gap: isMobile ? '40px' : 'clamp(48px, 8vw, 120px)',
            width: '100%',
            maxWidth: 1100,
            margin: '0 auto',
          }}
        >
          {/* Heading + supporting line — left column (platform hero styles) */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1
              style={{
                fontFamily: HEADING_FONT,
                fontWeight: 200,
                fontSize: 'var(--text-4xl)',
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
                color: COLORS.neutral2,
                margin: 0,
              }}
            >
              Partner Deal Registration
            </h1>
            <p style={{ ...bodyCopy, margin: '32px 0 0' }}>
              Thank you for registering your deal with Ent.
            </p>
            <p style={{ ...bodyCopy, margin: '1em 0 0' }}>
              Please fill out the information and we will be back in touch with you shortly for confirmation and acceptance.
            </p>
          </div>

          {/* HubSpot Embedded Form — right column. minHeight reserves space for
              the async-injected form so the footer below doesn't shift down
              when it renders (CLS). Measured at 1078px against the live form —
              a deal-registration form (customer + partner blocks), so roughly
              twice the demo form on /contact. One value covers both breakpoints
              because this embed lays out to a fixed height rather than
              reflowing with its width. Re-measure if fields change in HubSpot. */}
          <div style={{ width: isMobile ? '100%' : '492px', maxWidth: '492px', flexShrink: 0, minHeight: 1080 }}>
            <HubSpotEmbedForm formId={HUBSPOT_FORM_ID} targetId="partners-hubspot-form" />
          </div>
        </div>
      </div>
    </>
  )
}
